import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { SysmlApiAdapter } from '../api/api-adapter.js';
import type { SysMLDocument, FolderNode, TreeNode, ReorderPayload } from '../api/types.js';

// ---------------------------------------------------------------------------
// DocumentController — Document CRUD + folder tree + multi-tab state
//
// Ported from: document management section of sysmlModelerStore.ts
// Pattern: Lit ReactiveController
// ---------------------------------------------------------------------------

/** An open tab in the editor tab bar */
export interface OpenTab {
  documentId: string;
  /** Preview tabs are replaced when another document is single-clicked */
  isPreview: boolean;
}

/** Saved cursor state per document for tab restore */
export interface CursorState {
  line: number;
  column: number;
  scrollTop?: number;
}

export class DocumentController implements ReactiveController {
  private host: ReactiveControllerHost & HTMLElement;
  private api!: SysmlApiAdapter;

  // ── State ────────────────────────────────────────────────────────────────

  generation = 0;

  documents: SysMLDocument[] = [];
  currentDocument: SysMLDocument | null = null;
  folders: FolderNode[] = [];
  loading = false;
  isSaving = false;

  // ── Multi-tab state ────────────────────────────────────────────────────

  /** Ordered list of open tabs */
  openTabs: OpenTab[] = [];

  /** ID of the active document (shown in the editor) */
  activeDocumentId: string | null = null;

  /** Per-document cursor positions for tab restore */
  cursorPositions: Map<string, CursorState> = new Map();

  constructor(host: ReactiveControllerHost & HTMLElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {}
  hostDisconnected(): void {}

  // ── Computed ─────────────────────────────────────────────────────────────

  get explorerNodes(): TreeNode[] {
    return this._buildExplorerTree();
  }

  get hasDocuments(): boolean {
    return this.documents.length > 0;
  }

  get currentDocumentName(): string {
    return this.currentDocument?.name ?? 'Untitled';
  }

  /** IDs of all open documents in tab order */
  get openDocumentIds(): string[] {
    return this.openTabs.map((t) => t.documentId);
  }

  // ── Multi-tab Actions ─────────────────────────────────────────────────

  /**
   * Open a document as a tab.
   * - preview=true: replaces existing preview tab (single-click behavior)
   * - preview=false: pins the tab (double-click or edit behavior)
   * Returns the opened document, or null if not found.
   */
  openDocument(id: string, preview = false): SysMLDocument | null {
    const doc = this.documents.find((d) => d.id === id);
    if (!doc) return null;

    // Check if already open
    const existingIdx = this.openTabs.findIndex((t) => t.documentId === id);
    if (existingIdx >= 0) {
      // Already open — just activate it and optionally pin it
      if (!preview) {
        this.openTabs[existingIdx] = { ...this.openTabs[existingIdx], isPreview: false };
      }
      this.activeDocumentId = id;
      this.currentDocument = doc;
      this._update();
      return doc;
    }

    // If opening as preview, replace any existing preview tab
    if (preview) {
      const previewIdx = this.openTabs.findIndex((t) => t.isPreview);
      if (previewIdx >= 0) {
        this.openTabs[previewIdx] = { documentId: id, isPreview: true };
      } else {
        this.openTabs.push({ documentId: id, isPreview: true });
      }
    } else {
      this.openTabs.push({ documentId: id, isPreview: false });
    }

    this.activeDocumentId = id;
    this.currentDocument = doc;
    this._update();
    return doc;
  }

  /** Close a tab by document ID. Returns the new active document ID, or null. */
  closeDocument(id: string): string | null {
    const idx = this.openTabs.findIndex((t) => t.documentId === id);
    if (idx < 0) return this.activeDocumentId;

    this.openTabs.splice(idx, 1);

    // If we closed the active tab, switch to an adjacent one
    if (this.activeDocumentId === id) {
      if (this.openTabs.length === 0) {
        this.activeDocumentId = null;
        this.currentDocument = null;
      } else {
        // Pick the tab at the same index, or the last one
        const newIdx = Math.min(idx, this.openTabs.length - 1);
        this.activeDocumentId = this.openTabs[newIdx].documentId;
        this.currentDocument = this.documents.find((d) => d.id === this.activeDocumentId) ?? null;
      }
    }

    this._update();
    return this.activeDocumentId;
  }

  /** Pin a preview tab (make it non-preview) */
  pinDocument(id: string): void {
    const tab = this.openTabs.find((t) => t.documentId === id);
    if (tab && tab.isPreview) {
      tab.isPreview = false;
      this._update();
    }
  }

  /** Reorder tabs by moving sourceId to the position of targetId */
  reorderTabs(sourceId: string, targetId: string): void {
    const sourceIdx = this.openTabs.findIndex((t) => t.documentId === sourceId);
    const targetIdx = this.openTabs.findIndex((t) => t.documentId === targetId);
    if (sourceIdx < 0 || targetIdx < 0 || sourceIdx === targetIdx) return;

    const [moved] = this.openTabs.splice(sourceIdx, 1);
    this.openTabs.splice(targetIdx, 0, moved);
    this._update();
  }

  /** Save cursor position for a document (called before tab switch) */
  saveCursorPosition(docId: string, cursor: CursorState): void {
    this.cursorPositions.set(docId, cursor);
  }

  /** Update in-memory content for an open document (used by editor valueChange) */
  updateDocumentContent(docId: string, content: string): void {
    const doc = this.documents.find((d) => d.id === docId);
    if (!doc || doc.content === content) return;

    doc.content = content;
    if (this.currentDocument?.id === docId) {
      this.currentDocument = { ...this.currentDocument, content };
    }
    this._update();
  }

  /** Get saved cursor position for a document */
  getCursorPosition(docId: string): CursorState | undefined {
    return this.cursorPositions.get(docId);
  }

  /** Cycle to the next open tab. Returns the new active document, or null. */
  cycleTab(direction: 'next' | 'prev' = 'next'): SysMLDocument | null {
    if (this.openTabs.length <= 1) return this.currentDocument;
    const currentIdx = this.openTabs.findIndex((t) => t.documentId === this.activeDocumentId);
    if (currentIdx < 0) return this.currentDocument;

    const delta = direction === 'next' ? 1 : -1;
    const newIdx = (currentIdx + delta + this.openTabs.length) % this.openTabs.length;
    const newId = this.openTabs[newIdx].documentId;

    this.activeDocumentId = newId;
    this.currentDocument = this.documents.find((d) => d.id === newId) ?? null;
    this._update();
    return this.currentDocument;
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Initialize with an API instance; loads documents + folders */
  async initialize(api: SysmlApiAdapter): Promise<void> {
    this.api = api;
    this.loading = true;
    this._update();

    try {
      const [docs, folders] = await Promise.all([
        api.listDocuments(),
        api.getFolderTree(),
      ]);
      this.documents = docs;
      this.folders = folders;

      // Auto-open the first document as a pinned tab
      if (docs.length > 0) {
        this.currentDocument = docs[0];
        this.activeDocumentId = docs[0].id;
        this.openTabs = [{ documentId: docs[0].id, isPreview: false }];
      }
    } catch (err: unknown) {
      console.error('[sysml-mfe] Failed to initialize documents:', err);
    } finally {
      this.loading = false;
      this._update();
    }
  }

  /** Select a document by id (opens as preview tab) */
  async selectDocument(detail: { id: string; preview?: boolean }): Promise<void> {
    this.openDocument(detail.id, detail.preview ?? true);
  }

  /** Create a new document, optionally inside a folder */
  async createDocument(detail: { name?: string; parentId?: string } = {}): Promise<SysMLDocument> {
    const name = detail.name ?? 'New Document';
    const doc = await this.api.createDocument(name);
    if (detail.parentId) {
      doc.folderId = detail.parentId;
      await this.api.updateDocument(doc.id, { folderId: detail.parentId });
    }
    this.documents = [...this.documents, doc];
    this.currentDocument = doc;
    // New documents are always pinned (non-preview)
    this.openTabs.push({ documentId: doc.id, isPreview: false });
    this.activeDocumentId = doc.id;
    this._update();
    return doc;
  }

  /** Rename a document or folder */
  async renameDocument(detail: { node: TreeNode; newName?: string }): Promise<void> {
    const { node, newName } = detail;
    if (!newName) return;

    if (node.type === 'document') {
      await this.api.updateDocument(node.id, { name: newName });
      const doc = this.documents.find((d) => d.id === node.id);
      if (doc) doc.name = newName;
      if (this.currentDocument?.id === node.id) {
        this.currentDocument = { ...this.currentDocument, name: newName };
      }
    } else {
      this._renameFolderInTree(this.folders, node.id, newName);
      await this.api.updateFolderTree(this.folders);
    }
    this._update();
  }

  /** Delete a document or folder */
  async deleteDocument(detail: { node: TreeNode }): Promise<void> {
    const { node } = detail;

    if (node.type === 'document') {
      await this.api.deleteDocument(node.id);
      this.documents = this.documents.filter((d) => d.id !== node.id);
      // Also close the tab if open
      this.closeDocument(node.id);
      this.cursorPositions.delete(node.id);
    } else {
      this._removeFolderFromTree(this.folders, node.id);
      await this.api.updateFolderTree(this.folders);
    }
    this._update();
  }

  /** Save the current document with updated content */
  async saveCurrentDocument(content?: string): Promise<void> {
    if (!this.currentDocument) return;
    this.isSaving = true;
    this._update();

    try {
      const patch: Partial<SysMLDocument> = {};
      if (content !== undefined) patch.content = content;
      const updated = await this.api.updateDocument(this.currentDocument.id, patch);
      this.currentDocument = updated;

      // Update in list
      const idx = this.documents.findIndex((d) => d.id === updated.id);
      if (idx >= 0) this.documents[idx] = updated;
    } catch (err: unknown) {
      console.error('[sysml-mfe] Save failed:', err);
    } finally {
      this.isSaving = false;
      this._update();
    }
  }

  /** Create a new folder */
  async createFolder(name: string, parentId?: string): Promise<FolderNode> {
    const folder: FolderNode = {
      id: crypto.randomUUID(),
      name,
      parentId: parentId ?? null,
      children: [],
      documentIds: [],
    };

    if (parentId) {
      const parent = this._findFolder(this.folders, parentId);
      if (parent) parent.children.push(folder);
    } else {
      this.folders.push(folder);
    }

    await this.api.updateFolderTree(this.folders);
    this._update();
    return folder;
  }

  /** Reorder a node (document or folder) via drag-drop */
  async reorderNode(detail: ReorderPayload): Promise<void> {
    const doc = this.documents.find((d) => d.id === detail.nodeId);
    if (doc) {
      doc.folderId = detail.newParentId;
      await this.api.updateDocument(doc.id, { folderId: detail.newParentId });
    }
    this._update();
  }

  // ── Internal: tree building ──────────────────────────────────────────────

  private _buildExplorerTree(): TreeNode[] {
    const rootNodes: TreeNode[] = [];

    // Build folder tree nodes
    const buildFolderNode = (folder: FolderNode): TreeNode => ({
      id: folder.id,
      name: folder.name,
      type: 'folder',
      parentId: folder.parentId,
      children: [
        ...folder.children.map(buildFolderNode),
        ...this.documents
          .filter((d) => d.folderId === folder.id)
          .map((d) => this._docToTreeNode(d)),
      ],
      data: folder,
    });

    // Add top-level folders
    for (const folder of this.folders) {
      rootNodes.push(buildFolderNode(folder));
    }

    // Add top-level documents (not in any folder)
    for (const doc of this.documents.filter((d) => !d.folderId)) {
      rootNodes.push(this._docToTreeNode(doc));
    }

    return rootNodes;
  }

  private _docToTreeNode(doc: SysMLDocument): TreeNode {
    return {
      id: doc.id,
      name: doc.name,
      type: 'document',
      parentId: doc.folderId,
      children: [],
      data: doc,
    };
  }

  private _findFolder(tree: FolderNode[], id: string): FolderNode | null {
    for (const node of tree) {
      if (node.id === id) return node;
      const found = this._findFolder(node.children, id);
      if (found) return found;
    }
    return null;
  }

  private _renameFolderInTree(tree: FolderNode[], id: string, name: string): void {
    for (const node of tree) {
      if (node.id === id) {
        node.name = name;
        return;
      }
      this._renameFolderInTree(node.children, id, name);
    }
  }

  private _removeFolderFromTree(tree: FolderNode[], id: string): boolean {
    const idx = tree.findIndex((n) => n.id === id);
    if (idx >= 0) {
      tree.splice(idx, 1);
      return true;
    }
    return tree.some((n) => this._removeFolderFromTree(n.children, id));
  }

  private _update(): void {
    this.generation++;
    this.host.requestUpdate();
  }
}
